import { create } from 'zustand'
import type { ChatMessage } from '../types'
import type { ConversationListItem } from '@/shared/types'

interface ChatStoreState {
  // Per-conversation message storage — keyed by conversation ID
  messagesMap: Map<number, ChatMessage[]>
  conversations: ConversationListItem[]
  currentConversationId: number | null
  // Tracks which conversation IDs have an active stream in flight
  streamingConversationIds: Set<number>
  // Tracks which streaming conversations have received their first token
  firstTokenReceivedIds: Set<number>
  isLoadingConversations: boolean
  isLoadingMessages: boolean
  // Guard flag to prevent re-fetching conversations on every mount
  conversationsLoaded: boolean
  // Suggested agent ID for contexts like orchestration
  suggestedAgentDefinitionId: number | null
  // Per-conversation agent locking — populated from conversation list
  agentPerConversation: Map<number, number | null>

  // Actions
  setMessagesMap: (messagesMap: Map<number, ChatMessage[]>) => void
  setMessagesForConversation: (convId: number, updater: (prev: ChatMessage[]) => ChatMessage[]) => void
  setConversations: (conversations: ConversationListItem[]) => void
  setCurrentConversationId: (id: number | null) => void
  setStreamingConversationIds: (ids: Set<number>) => void
  setFirstTokenReceivedIds: (ids: Set<number>) => void
  setIsLoadingConversations: (loading: boolean) => void
  setIsLoadingMessages: (loading: boolean) => void
  setConversationsLoaded: (loaded: boolean) => void
  setSuggestedAgentDefinitionId: (id: number | null) => void
  // Bulk update for streaming state
  addStreamingConversation: (id: number) => void
  removeStreamingConversation: (id: number) => void
  addFirstTokenReceived: (id: number) => void
  removeFirstTokenReceived: (id: number) => void
  deleteConversation: (id: number) => void
  updateConversationTitle: (id: number, title: string) => void
  // Agent per conversation tracking
  getAgentForConversation: (id: number) => number | null
}

export const useChatStore = create<ChatStoreState>((set, get) => ({
  messagesMap: new Map(),
  conversations: [],
  currentConversationId: null,
  streamingConversationIds: new Set(),
  firstTokenReceivedIds: new Set(),
  isLoadingConversations: false,
  isLoadingMessages: false,
  conversationsLoaded: false,
  suggestedAgentDefinitionId: null,
  agentPerConversation: new Map(),

  setMessagesMap: (messagesMap) => set({ messagesMap }),

  setMessagesForConversation: (convId, updater) =>
    set((state) => {
      const prev = state.messagesMap.get(convId) ?? []
      const next = updater(prev)
      if (next === prev) return state
      const newMap = new Map(state.messagesMap)
      newMap.set(convId, next)
      return { messagesMap: newMap }
    }),

  setConversations: (conversations) => {
    // Build the agent per conversation map from the loaded conversations
    const agentMap = new Map<number, number | null>()
    for (const conv of conversations) {
      agentMap.set(conv.id, conv.agent_definition_id)
    }
    set({ conversations, agentPerConversation: agentMap })
  },

  setCurrentConversationId: (id) => set({ currentConversationId: id }),

  setStreamingConversationIds: (ids) => set({ streamingConversationIds: ids }),

  setFirstTokenReceivedIds: (ids) => set({ firstTokenReceivedIds: ids }),

  setIsLoadingConversations: (loading) => set({ isLoadingConversations: loading }),

  setIsLoadingMessages: (loading) => set({ isLoadingMessages: loading }),

  setConversationsLoaded: (loaded) => set({ conversationsLoaded: loaded }),

  setSuggestedAgentDefinitionId: (id) => set({ suggestedAgentDefinitionId: id }),

  addStreamingConversation: (id) =>
    set((state) => ({
      streamingConversationIds: new Set(state.streamingConversationIds).add(id),
    })),

  removeStreamingConversation: (id) =>
    set((state) => {
      const next = new Set(state.streamingConversationIds)
      next.delete(id)
      return { streamingConversationIds: next }
    }),

  addFirstTokenReceived: (id) =>
    set((state) => ({
      firstTokenReceivedIds: new Set(state.firstTokenReceivedIds).add(id),
    })),

  removeFirstTokenReceived: (id) =>
    set((state) => {
      const next = new Set(state.firstTokenReceivedIds)
      next.delete(id)
      return { firstTokenReceivedIds: next }
    }),

  deleteConversation: (id) =>
    set((state) => {
      const newMap = new Map(state.messagesMap)
      newMap.delete(id)
      return {
        messagesMap: newMap,
        currentConversationId: state.currentConversationId === id ? null : state.currentConversationId,
      }
    }),

  updateConversationTitle: (id, title) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === id ? { ...c, title } : c
      ),
    })),

  getAgentForConversation: (id) => get().agentPerConversation.get(id) ?? null,
}))
